export const cgmTestReliability = `

<br/>

<p><strong>Disclaimer:</strong> This test is not sponsored or influenced by a manufacturer. CGMs were tested from 9 to 24 December 2025. This is not a study and the data is not representative; it is only an n=1 comparison and subjective impressions focused on reliability and accuracy. Reliability of measurement was checked with data from 8–10 daily glucose checks using a Contour Plus One glucometer.</p>

<br/>

<p>The good news is that sensors have come a long way recently, improving in quality, increasing competition, and most of them are not a complete disaster, which could not have been said a few years ago (although there are still some outright disasters on the market).</p>

<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Short Summary</h3>

<p>A short summary on accuracy and measurement performance:</p>

<ul style="line-height: 1.8;">
  <li><strong>Most accurate measurements:</strong> (in this order) Syai Ultra, Caresens Air, Dexcom G6, Syai Tag, Yuwell</li>
  <li><strong>Fastest response to changes:</strong> Dexcom G7, Syai Ultra, FSL 2</li>
  <li><strong>Best at measuring at the edge of the range:</strong> Dexcom G7, Syai Ultra</li>
  <li><strong>Smoothest measurement:</strong> Dexcom G6, Caresens Air, Syai Tag, FSL, Yuwell Anytime</li>
  <li><strong>Measurement frequency:</strong> Some sensors measure every minute (FSL), others every three minutes (Hippo, Yuwell), others every five minutes… but this is only good for getting a blood glucose trend; in terms of loop function, it's rather useless because insulin is still much slower.</li>
  <li><strong>Ended prematurely:</strong> Glunovo D3 (right after the insertion), Dexcom G7 (8 d and 7 h, sensor error), Hippo H1 (8 d and 11 h, ended manually, only LO readings), Caresens Air (14 d and 10 h)</li>
  <li><strong>Best signal strength:</strong> I tested them in an open space, and Yuwell was the first to lose signal (8 m), while all the others were able to transmit data over a distance of 14 m</li>
</ul>

<br/>

<p>The following table shows the ranking of all sensors in deviation from manual glucose measurements and MARD over 11 days. </p>

<div style="display: flex; flex-direction: column; align-items: center; margin: 2rem 0;">
  <img 
    src="/assets/blogs/sensorcomparison/sensorrankingMARDdeviation.png" 
    alt="Sensor Ranking by MARD and Deviation" 
    style="max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
  />
  <span style="font-size: 0.92em; color: #555; text-align: center;">
    MARD over time over 11 days, the lower the MARD the higher the accuracy. Glunovo D3 died prematurely.
  </span>
</div>

<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Detailed results</h3>

<p>These graphs show the MARD trend over time, i.e., whether the sensors become more accurate or less accurate over their lifetime compared to blood measurements. (Descending curve = sensor becomes more accurate over time, ascending curve = sensor becomes less and less accurate over time)</p>

<br/>

<p>Dexcom G6, Syai Tag, and Caresens Air gradually became more accurate, while SmartGuide and Hippo improved dramatically over time. Dexcom G7 and FSL, on the other hand, deteriorated dramatically over time. Yuwell deteriorated imperceptibly over time. Syai Ultra remained completely stable throughout the entire period, with its accuracy remaining virtually unchanged over its lifetime.</p>

<br/>


<div style="display: flex; flex-direction: column; align-items: center; gap: 1.5rem; margin-bottom: 2.5rem;">

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Dexcom</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/dexcomG6MARD.png" 
          alt="Dexcom G6 MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Dexcom G6</div>
      </div>
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/dexcomG7MARD.png" 
          alt="Dexcom G7 MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Dexcom G7</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Freestyle Libre</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/fsl2MARD.png" 
          alt="Freestyle Libre 2 MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">FSL 2</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Syai</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/syaitagMARD.png" 
          alt="Syai TAG MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Syai TAG</div>
      </div>
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/syaiultraMARD.png" 
          alt="Syai Ultra MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Syai Ultra</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Caresens</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/caresensairMARD.png" 
          alt="Caresens Air MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Caresens Air</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Yuwell</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/yuwellanytimeMARD.png" 
          alt="Yuwell Anytime MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Yuwell Anytime</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Accu-Chek</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/accucheksmartguideMARD.png" 
          alt="Accu-Chek SmartGuide MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Accu-Chek SmartGuide</div>
      </div>
    </div>
  </div>

  <div style="width: 100%; max-width: 1170px;">
    <h4 style="margin: 1.2em 0 0.7em; color: #1976d2; font-size: 1.15em;">Hippo</h4>
    <div style="display: flex; flex-wrap: wrap; gap: 2.25rem; justify-content: center;">
      <div style="text-align: center;">
        <img 
          src="/assets/blogs/sensorcomparison/hippoh1MARD.png" 
          alt="Hippo H1 MARD Trend"
          style="width: 375px; max-width: 100%; border: 1px solid #ccc; border-radius: 8px; margin-bottom: 0.5em;"
        />
        <div style="font-size: 0.9em; color: #555;">Hippo H1</div>
      </div>
    </div>
  </div>

</div>








<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">Favorites for Looping</h3>

<p>The most pressing question — given that the open-source diabetes Facebook groups are already discussing the end of G6 production, announced for July 1, 2026 (although, according to Dexcom, this only applies to the US for now, but we all expected it to happen sooner or later anyway).</p>

<p><strong>Would I choose from the test possible G6 successor to run in my loop?</strong></p>

<p>Yes, with a clear conscience, I would choose from my two favorites, Syai and Caresens.</p>

<br/>

<div style="display: flex; justify-content: center; margin-top: 1.5em; margin-bottom: 1em;">
    <a
      href="/blog/details/Insights-from-Testing-10-CGMs---How-They-Stack-Up-in-Daily-Use"
      rel="noopener"
      style="color: #1976d2; font-size: 1em; text-align: center; text-decoration: underline; font-weight: 500; padding: 0.75em 1.5em; border-radius: 6px; background: rgba(25, 118, 210, 0.07); transition: background 0.2s;"
      data-mouseover-bg="rgba(25, 118, 210, 0.13)"
      data-mouseout-bg="rgba(25, 118, 210, 0.07)"
    >
      <span style="font-weight: 700;">Interested in user experience? See the comparison on CGM usability &rarr;</span>
    </a>
  </div>

<br/>

<p style="font-size: 0.9em; color: #666; font-style: italic;">
Written by <a href="/blog/details/Introducing-Luk%C3%A1%C5%A1-Tr%C4%8Dka" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Lukáš Trčka</a>, reviewed by <a href="/blog/details/Introducing-Kelvin-Kramp" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Kelvin Kramp</a> (MD, PhD).
</p>

<div style="display: flex; justify-content: center; padding: 1.5rem 0;">
<img src="https://miro.medium.com/v2/resize:fit:1400/format:webp/1*0_i4kx_n4GqjBQ5vSF9E4w.png" alt="The Opensource.clinic team" style="max-width: 100%; height: auto;" />
</div>


<!-- blog-contact-footer -->
`;
